// @ts-nocheck
/**
 * Lens Configurator Drawer Manager - Versión Optimizada y Escalable
 * Maneja apertura, cierre y funcionalidad del drawer lateral
 */

class LensDrawerManager {
  constructor(options = {}) {
    // Configuración con valores por defecto
    this.config = {
      maxInitWaitTime: 3000,
      pollInterval: 120,
      autoCreateTrigger: false,
      ...options
    };

    // Estado principal
    this.drawer = null;
    this.overlay = null;
    this.isOpen = false;
    this.configurator = null;
    this._initialized = false;

    // Estados de control
    this._suppressNext = false;
    this._origNextStep = null;
    this._footerSyncInterval = null;

    // Cache de elementos para mejor performance
    this.elements = {
      buttons: {},
      footer: {},
      progress: {}
    };

    // Pasos configurables (fácil de escalar)
    this.steps = [
      { id: 'lens-type', name: 'Tipo de Cristal', required: true },
      { id: 'progressive-type', name: 'Tipo Progresivo', required: false },
      { id: 'thickness', name: 'Grosor', required: true },
      { id: 'treatments', name: 'Tratamientos', required: false }
      // Para añadir más pasos: agregar aquí y el HTML se actualizará automáticamente
    ];

    this.init();
  }

  init() {
    if (this._initialized) {
      console.warn('⚠️ Drawer Manager ya estaba inicializado');
      return;
    }

    console.log('🔄 Inicializando Lens Drawer Manager...');
    
    try {
      this.createDrawer();
      
      if (!this.drawer || !this.overlay) {
        throw new Error('No se pudieron crear elementos del drawer');
      }

      this.setupEventListeners();
      this.initConfigurator();
      this._initialized = true;
      
      console.log('✅ Lens Drawer Manager inicializado correctamente');
    } catch (error) {
      console.error('❌ Error inicializando Drawer Manager:', error);
    }
  }

  createDrawer() {
    // Crear elementos principales
    this.drawer = document.createElement('div');
    this.drawer.className = 'lens-drawer';
    this.drawer.setAttribute('role', 'dialog');
    this.drawer.setAttribute('aria-labelledby', 'drawer-title');
    this.drawer.innerHTML = this.generateDrawerHTML();

    this.overlay = document.createElement('div');
    this.overlay.className = 'lens-drawer__overlay';
    this.overlay.setAttribute('aria-hidden', 'true');

    // Inyectar en el DOM
    document.body.append(this.overlay, this.drawer);
    

    // Cachear elementos y configurar eventos
    this.cacheElements();
    this.setupDrawerEvents();
  }

  generateDrawerHTML() {
    return `
      <div class="lens-drawer__header">
        <button type="button" id="header-prev-drawer" class="header-nav-btn prev" disabled aria-label="Paso anterior">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path d="M15 18l-6-6 6-6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </button>
        
        <div class="drawer-progress">
          <div class="progress-text" id="progress-text-drawer">Paso 1 de ${this.steps.length}</div>
        </div>
        
        <button type="button" id="header-next-drawer" class="header-nav-btn next" aria-label="Siguiente paso">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path d="M9 18l6-6-6-6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </button>
        
        <button class="lens-drawer__close" aria-label="Cerrar configurador">×</button>
      </div>
      
      <div class="drawer-progress-bar-wrapper">
        <div class="progress-bar">
          <div class="progress-fill" id="progress-fill-drawer"></div>
        </div>
      </div>
      
      <div class="lens-drawer__content">
        <div id="lens-configurator-container"></div>
      </div>
      
      <div class="lens-drawer__footer">
        <div class="footer-summary">
          ${this.generateSummarySections()}
        </div>
        <button class="cart-button" id="add-to-cart-btn" aria-live="polite">
          <span class="button-text">Siguiente</span>
          <span class="button-price" id="button-total">$0</span>
        </button>
      </div>
    `;
  }

  generateSummarySections() {
    return this.steps.map(step => {
      const hasSelection = step.id !== 'base';
      const selectionHTML = hasSelection ? `<span class="selection" id="${step.id}-name"></span>` : '';
      
      return `
        <div class="summary-section" id="${step.id}-summary">
          <div class="summary-info">
            <!-- <span class="label">${step.name}</span> -->
            ${selectionHTML}
          </div>
          <span class="value" id="${step.id}-price">$0</span>
        </div>
      `;
    }).join('');
  }

  cacheElements() {
    // Cachear botones principales
    this.elements.buttons = {
      close: this.drawer.querySelector('.lens-drawer__close'),
      cart: this.drawer.querySelector('#add-to-cart-btn'),
      headerPrev: this.drawer.querySelector('#header-prev-drawer'),
      headerNext: this.drawer.querySelector('#header-next-drawer')
    };

    // Cachear elementos de progreso
    this.elements.progress = {
      text: this.drawer.querySelector('#progress-text-drawer'),
      fill: this.drawer.querySelector('#progress-fill-drawer')
    };

    // Cachear elementos del footer dinámicamente
    this.elements.footer = {};
    this.steps.forEach(step => {
      this.elements.footer[`${step.id}Price`] = this.drawer.querySelector(`#${step.id}-price`);
      this.elements.footer[`${step.id}Name`] = this.drawer.querySelector(`#${step.id}-name`);
      this.elements.footer[`${step.id}Summary`] = this.drawer.querySelector(`#${step.id}-summary`);
    });
    
    this.elements.footer.buttonTotal = this.drawer.querySelector('#button-total');
  }

  setupDrawerEvents() {
    const { buttons } = this.elements;
    
    // Event listeners con delegación
    buttons.close?.addEventListener('click', () => this.close());
    buttons.cart?.addEventListener('click', (e) => this.handleFooterButton(e));
    buttons.headerPrev?.addEventListener('click', (e) => this.handleHeaderPrev(e));
    buttons.headerNext?.addEventListener('click', (e) => this.handleHeaderNext(e));
    this.overlay?.addEventListener('click', () => this.close());
  }

  setupEventListeners() {
    // Escape key para cerrar
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen) this.close();
    });

    // Prevenir scroll del body cuando drawer está abierto
    this.drawer?.addEventListener('wheel', (e) => e.stopPropagation());
  }

  async initConfigurator() {
    const start = Date.now();
    
    while (Date.now() - start < this.config.maxInitWaitTime) {
      if (await this.tryInitializeConfigurator()) return;
      await this.delay(this.config.pollInterval);
    }
    
    console.warn('⚠️ No se encontró el configurador después del tiempo de espera');
  }

  async tryInitializeConfigurator() {
    try {
      const existingConfigurator = document.querySelector('#lens-configurator-simple');
      const drawerContent = this.drawer?.querySelector('.lens-drawer__content');

      // Caso 1: Reutilizar instancia global existente
      if (window.lensConfiguratorSimple) {
        this.configurator = window.lensConfiguratorSimple;
        if (drawerContent && existingConfigurator && !drawerContent.contains(existingConfigurator)) {
          drawerContent.appendChild(existingConfigurator);
        }
        console.log('✅ Reutilizando configurador existente');
        return true;
      }

      // Caso 2: Instanciar nuevo configurador
      if (window.LensConfiguratorSimple) {
        if (!existingConfigurator && drawerContent) {
          const container = document.createElement('div');
          container.id = 'lens-configurator-simple';
          drawerContent.appendChild(container);
        }
        
        this.configurator = new window.LensConfiguratorSimple();
        console.log('✅ Configurador instanciado correctamente');
        return true;
      }

      return false;
    } catch (error) {
      console.warn('⚠️ Error en inicialización de configurador:', error);
      return false;
    }
  }

  // ========== MANEJO DE EVENTOS ==========

  handleFooterButton(e) {
    e.stopImmediatePropagation();
    e.preventDefault();
    this.attemptNextOrAdd();
  }

  handleHeaderPrev(e) {
    e.stopImmediatePropagation();
    if (this.configurator?.previousStep) {
      this.configurator.previousStep();
    }
  }

  handleHeaderNext(e) {
    this.attemptNextOrAdd(e);
  }

  attemptNextOrAdd(e) {
    if (e) {
      e.stopImmediatePropagation();
      e.preventDefault();
    }

    if (!this.configurator) return;

    // Debug: tracear estado para ayudar a depuración en entorno real
    try {
      console.debug('lens-drawer: attemptNextOrAdd', {
        currentStep: this.configurator.currentStep,
        totalSteps: this.configurator.steps?.length || this.configurator.totalSteps,
        stepInstancesCount: this.configurator.stepInstances?.length
      });
    } catch (err) {}

    try {
      const totalSteps = this.configurator.steps?.length || this.configurator.totalSteps || 0;
      const currentStep = this.configurator.currentStep || 0;
      const isLastStep = totalSteps > 0 ? (currentStep >= totalSteps - 1) : false;

      if (!isLastStep) {
        if (!this.canProceedToNextStep(currentStep)) {
          console.debug('lens-drawer: cannot proceed, showing modal for step', currentStep);
          this.showSelectionRequiredModal();
          return;
        }

        if (this.configurator.nextStep) {
          this.configurator.nextStep();
          return;
        }

      }
    } catch (err) {
      // fallthrough to add to cart
    }

    // Default: perform add to cart
    this.handleAddToCart();
  }

  canProceedToNextStep(stepIndex) {
    try {
      const currentInstance = this.configurator.stepInstances?.[stepIndex];
      
      if (!currentInstance) return false;

      if (typeof currentInstance.canProceed === 'function') {
        return Boolean(currentInstance.canProceed());
      }

      if (typeof currentInstance.getSummary === 'function') {
        const summary = currentInstance.getSummary();
        const value = summary?.value ? String(summary.value).trim() : '';
        return value && !['No seleccionado', 'Ninguno', 'No configurado'].includes(value);
      }

      return false;
    } catch (error) {
      return false;
    }
  }

  // ========== GESTIÓN DEL DRAWER ==========

  open() {
    if (this.isOpen || !this.drawer || !this.overlay) {
      console.error('❌ No se puede abrir el drawer');
      return;
    }

    this.isOpen = true;
    this.overlay.classList.add('visible');
    this.drawer.classList.add('open');
    document.body.style.overflow = 'hidden';
    this.drawer.focus();
    
    document.dispatchEvent(new CustomEvent('lens-drawer:opened'));
    this.startFooterSync();
    
    console.log('📂 Drawer abierto');
  }

  close() {
    if (!this.isOpen) return;

    this.isOpen = false;
    this.overlay?.classList.remove('visible');
    this.drawer?.classList.remove('open');
    document.body.style.overflow = '';

    document.dispatchEvent(new CustomEvent('lens-drawer:closed'));
    this.cleanup();

    console.log('📂 Drawer cerrado');
  }

  cleanup() {
    this.stopFooterSync();
    this.restoreOriginalNextStep();
  }

  toggle() {
    this.isOpen ? this.close() : this.open();
  }

  // ========== SINCRONIZACIÓN DEL FOOTER ==========

  startFooterSync() {
    if (this._footerSyncInterval) return;
    
    this._footerSyncInterval = setInterval(() => {
      this.updateFooterButtonLabel();
    }, 150);
  }

  stopFooterSync() {
    if (this._footerSyncInterval) {
      clearInterval(this._footerSyncInterval);
      this._footerSyncInterval = null;
    }
  }

  updateFooterButtonLabel() {
    const btn = this.elements.buttons.cart;
    if (!btn) return;

    let label = 'Siguiente';
    let showIcon = true;

    if (this.configurator) {
      try {
        const totalSteps = this.configurator.steps?.length || this.configurator.totalSteps || 0;
        const currentStep = this.configurator.currentStep || 0;
        const isLastStep = totalSteps > 0 ? (currentStep >= totalSteps - 1) : false;
        
        if (isLastStep) {
          label = 'Finalizar Compra';
          showIcon = false;
        }
      } catch (error) {
        // Mantener valores por defecto
      }
    }

    const textSpan = btn.querySelector('.button-text');
    if (textSpan) {
      if (showIcon) {
        textSpan.innerHTML = `${label} <svg class="footer-next-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M9 18l6-6-6-6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
      } else {
        textSpan.textContent = label;
      }
    }
  }

  // ========== ACTUALIZACIÓN DE UI ==========

  updateFooterSummary(summaryData) {
    if (!this.elements.footer || !summaryData || typeof summaryData !== 'object') return;

    // Actualizar precios y nombres dinámicamente
    this.steps.forEach(step => {
      const camelKey = this.toCamelCase(step.id);
      const priceKey = `${camelKey}Price`;
      const nameKey = `${camelKey}Name`;

      const priceEl = this.elements.footer[`${step.id}Price`];
      const nameEl = this.elements.footer[`${step.id}Name`];
      const priceText = summaryData[priceKey] || summaryData[`${step.id}Price`] || '$0';
      const nameText = summaryData[nameKey] || summaryData[`${step.id}Name`] || '';
      const summaryEl = this.elements.footer[`${step.id}Summary`];

      if (priceEl) {
        priceEl.textContent = priceText;
      }
      if (nameEl) {
        nameEl.textContent = nameText;
      }
      if (summaryEl) {
        this.toggleSummarySection(summaryEl, step, summaryData);
      }
    });

    // Actualizar total
    if (this.elements.footer.buttonTotal) {
      this.elements.footer.buttonTotal.textContent = summaryData.totalPrice || '$0';
    }
  }

  toggleSummarySection(element, step, summaryData) {
    const camelKey = this.toCamelCase(step.id);
    const pascalKey = this.toPascalCase(step.id);
    const hasSelection = summaryData[`has${pascalKey}`];
    const price = summaryData[`${camelKey}Price`] || summaryData[`${step.id}Price`];
    
    const hasPrice = price && !this.isZeroPrice(price);

    const shouldShow = hasSelection || hasPrice;

    shouldShow ? element.classList.add('visible') : element.classList.remove('visible');
  }

  toCamelCase(str) {
    const value = typeof str === 'string' ? str : '';
    if (!value) return '';
    return value
      .replace(/[^a-zA-Z0-9]+(.)/g, (_, chr) => (chr ? chr.toUpperCase() : ''))
      .replace(/^[A-Z]/, match => match.toLowerCase());
  }

  toPascalCase(str) {
    const camel = this.toCamelCase(str);
    if (!camel) return '';
    return camel.charAt(0).toUpperCase() + camel.slice(1);
  }

  isZeroPrice(value) {
    if (value === null || value === undefined) return true;
    if (typeof value === 'number') return value === 0;

    const str = String(value).trim();
    if (!str) return true;

    if (/incluido/i.test(str)) return true;

    const numeric = parseFloat(str.replace(/[^0-9,.-]/g, '').replace(',', '.'));
    if (isNaN(numeric)) return false;
    return Math.abs(numeric) < 0.00001;
  }

  // ========== CARRITO Y MODALES ==========

  async handleAddToCart() {
    if (!this.configurator?.handleAddToCart) return;

    const btn = this.elements.buttons.cart;
    try {
      this.showButtonLoading(btn);
      
      const result = this.configurator.handleAddToCart();
      
      if (result && typeof result.then === 'function') {
        await result;
      }
      
      this.close();
    } catch (error) {
      console.error('Error agregando al carrito:', error);
      this.showErrorModal('Error al agregar al carrito');
    } finally {
      this.hideButtonLoading(btn);
    }
  }

  showButtonLoading(btn) {
    if (!btn) return;
    
    btn.disabled = true;
    btn.setAttribute('aria-busy', 'true');
    btn.classList.add('loading');
    
    if (!btn.querySelector('.btn-spinner')) {
      const spinner = document.createElement('span');
      spinner.className = 'btn-spinner';
      spinner.setAttribute('aria-hidden', 'true');
      btn.prepend(spinner);
    }
  }

  hideButtonLoading(btn) {
    if (!btn) return;
    
    btn.disabled = false;
    btn.removeAttribute('aria-busy');
    btn.classList.remove('loading');
    
    const spinner = btn.querySelector('.btn-spinner');
    if (spinner) spinner.remove();
  }

  showSelectionRequiredModal() {
    if (document.getElementById('lens-selection-required-modal')) return;

    this.showModal({
      id: 'lens-selection-required-modal',
      title: 'Selecciona una opción',
      message: 'Por favor selecciona al menos una opción en este paso antes de continuar.',
      buttonText: 'Aceptar'
    });
  }

  showErrorModal(message) {
    this.showModal({
      id: 'lens-error-modal',
      title: 'Error',
      message: message,
      buttonText: 'Entendido'
    });
  }

  showModal({ id, title, message, buttonText }) {
    // Añadir estilos mínimos para el modal si no están presentes
    if (!document.getElementById('lens-drawer-modal-styles')) {
      const mstyle = document.createElement('style');
      mstyle.id = 'lens-drawer-modal-styles';
      mstyle.innerHTML = `
        .lens-modal-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.45); display:flex; align-items:center; justify-content:center; z-index:3000; }
        .lens-modal { background: #fff; color: #222; max-width: 420px; width: 94%; border-radius: 8px; box-shadow: 0 10px 40px rgba(0,0,0,0.2); overflow: hidden; }
        .lens-modal__body { padding: 18px; }
        .lens-modal__body h3 { margin: 0 0 8px 0; font-size: 18px; }
        .lens-modal__body p { margin: 0; font-size: 14px; color: #444; }
        .lens-modal__footer { padding: 12px; text-align: right; background: #f7f7f7; }
        .lens-modal__close { background: #007bff; color: white; border: none; padding: 8px 12px; border-radius: 4px; cursor: pointer; }
      `;
      document.head.appendChild(mstyle);
    }
    const modal = document.createElement('div');
    modal.id = id;
    modal.className = 'lens-modal-overlay';
    modal.innerHTML = `
      <div class="lens-modal" role="dialog" aria-labelledby="modal-title">
        <div class="lens-modal__body">
          <h3 id="modal-title">${title}</h3>
          <p>${message}</p>
        </div>
        <div class="lens-modal__footer">
          <button class="lens-modal__close">${buttonText}</button>
        </div>
      </div>
    `;

    document.body.appendChild(modal);

    // Supress configurator.nextStep while modal is open to avoid races
    try {
      this._suppressNext = true;
      if (this.configurator && typeof this.configurator.nextStep === 'function' && !this._origNextStep) {
        this._origNextStep = this.configurator.nextStep.bind(this.configurator);
        const manager = this;
        this.configurator.nextStep = function() {
          if (manager._suppressNext) return; // noop while suppressed
          return manager._origNextStep();
        };
      }
    } catch (e) {}

    const closeModal = () => {
      if (modal && modal.parentNode) modal.parentNode.removeChild(modal);
      try { this.restoreOriginalNextStep(); } catch (e) {}
    };

    modal.addEventListener('click', (e) => e.target === modal && closeModal());
    modal.querySelector('.lens-modal__close')?.addEventListener('click', closeModal);
  }

  // ========== UTILIDADES ==========

  delay(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  restoreOriginalNextStep() {
    if (this.configurator && this._origNextStep) {
      this.configurator.nextStep = this._origNextStep;
      this._origNextStep = null;
    }
    this._suppressNext = false;
  }

  // ========== MÉTODOS PÚBLICOS ADICIONALES ==========

  refresh() {
    if (this.configurator?.refresh) {
      this.configurator.refresh();
    }
  }

  destroy() {
    this.close();
    this.stopFooterSync();
    
    if (this.drawer) {
      this.drawer.remove();
      this.drawer = null;
    }
    
    if (this.overlay) {
      this.overlay.remove();
      this.overlay = null;
    }
    
    this._initialized = false;
    console.log('🗑️ Drawer Manager destruido');
  }

  // Para añadir más pasos dinámicamente
  addStep(step) {
    this.steps.push(step);
    // En una implementación real, aquí regenerarías el HTML del summary
    console.log('➕ Paso añadido:', step);
  }
}

// ========== FUNCIONES GLOBALES PARA USO EXTERNO ==========

function createDrawerTrigger(text = 'Configurar Lentes', className = 'lens-drawer-trigger') {
  const button = document.createElement('button');
  button.className = className;
  button.innerHTML = `
    <svg class="lens-drawer-trigger-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
      <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
    </svg>
    ${text}
  `;
  
  return button;
}

function addDrawerTriggerToProduct(buttonText = 'Configurar Lentes') {
  if (document.querySelector('.lens-drawer-trigger')) {
    console.log('✅ Ya existe un trigger del drawer');
    return;
  }

  const addToCartSelectors = [
    '[name="add"]',
    '.btn-product',
    '.product-form__cart-submit', 
    '.product__add-to-cart',
    '.add-to-cart',
    '.cart-btn'
  ];

  const addToCartButton = addToCartSelectors
    .map(selector => document.querySelector(selector))
    .find(button => button !== null);

  if (addToCartButton) {
    const trigger = createDrawerTrigger(buttonText);
    
    trigger.addEventListener('click', () => {
      if (window.lensDrawerManager) {
        window.lensDrawerManager.open();
      }
    });

    addToCartButton.parentNode?.insertBefore(trigger, addToCartButton);
    console.log('✅ Trigger del drawer agregado');
    
    return trigger;
  }

  console.warn('⚠️ No se encontró botón de Add to Cart');
  return null;
}

function setupTriggerEventListeners() {
  const triggers = document.querySelectorAll('[data-lens-trigger="true"], .lens-drawer-trigger');
  
  triggers.forEach(trigger => {
    if (trigger.dataset.listenerAdded === 'true') return;
    
    trigger.addEventListener('click', function() {
      console.log('🔘 Botón Configurar Lentes clickeado');

      if (window.lensDrawerManager) {
        if (window.lensDrawerManager.configurator) {
          window.lensDrawerManager.open();
          return;
        }

        // Intentar inicializar si no hay configurador
        setTimeout(() => {
          if (!window.lensDrawerManager.configurator && window.LensConfiguratorSimple) {
            try {
              window.lensDrawerManager.configurator = new window.LensConfiguratorSimple();
              window.lensDrawerManager.open();
              return;
            } catch (error) {
              console.warn('⚠️ Error al instanciar configurador:', error);
            }
          }
          window.lensDrawerManager.open();
        }, 200);
        return;
      }

      // Fallback: inicializar manager si no existe
      if (window.LensDrawerManager) {
        window.lensDrawerManager = new window.LensDrawerManager();
        window.lensDrawerManager.open();
      } else {
        console.error('❌ LensDrawerManager no disponible');
      }
    });
    
    trigger.dataset.listenerAdded = 'true';
  });

  console.log(`✅ ${triggers.length} trigger(s) configurado(s)`);
}

// ========== INICIALIZACIÓN AUTOMÁTICA ==========

document.addEventListener('DOMContentLoaded', () => {
  console.log('🚀 DOM Content Loaded - Iniciando Drawer Manager...');

  // Crear manager global
  try {
    window.lensDrawerManager = new LensDrawerManager();
    console.log('✅ Drawer Manager creado globalmente');
  } catch (error) {
    console.error('❌ Error creando Drawer Manager:', error);
  }

  // Configurar event listeners en páginas de producto
  if (window.location.pathname.includes('/products/')) {
    setTimeout(() => {
      try {
        setupTriggerEventListeners();
        
        // Opcional: crear trigger automáticamente
        if (window.lensDrawerManager?.config?.autoCreateTrigger) {
          addDrawerTriggerToProduct();
        }
      } catch (error) {
        console.error('❌ Error configurando triggers:', error);
      }
    }, 500);
  }

  // Configurar triggers globales
  setTimeout(() => {
    try {
      setupTriggerEventListeners();
    } catch (error) {
      console.error('❌ Error configurando triggers globales:', error);
    }
  }, 1000);
});

// ========== EXPORTACIÓN PARA MÓDULOS ==========

if (typeof module !== 'undefined' && module.exports) {
  module.exports = {
    LensDrawerManager,
    createDrawerTrigger,
    addDrawerTriggerToProduct,
    setupTriggerEventListeners
  };
} else {
  // Exportar al ámbito global para Shopify
  window.LensDrawerManager = LensDrawerManager;
  window.createDrawerTrigger = createDrawerTrigger;
  window.addDrawerTriggerToProduct = addDrawerTriggerToProduct;
  window.setupTriggerEventListeners = setupTriggerEventListeners;
}